import { inject } from '@vercel/analytics';
import { prefersReducedMotion } from '../utils/motion-preference';

const SHOT_INTERVAL_MS = 2600;
const EXIT_TRANSITION_MS = 320;

function initPageEnter(): void {
  const site = document.querySelector<HTMLElement>('#site');

  if (!site) {
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      site.classList.add('page-ready');
    });
  });
}

function initCarousel(): void {
  const shots = document.querySelectorAll<HTMLElement>('.gbshot');
  const dots = document.querySelectorAll<HTMLElement>('.gbdot');

  if (shots.length === 0) {
    return;
  }

  let current = 0;

  setInterval(() => {
    shots[current].classList.remove('on');
    dots[current]?.classList.remove('on');

    current = (current + 1) % shots.length;

    shots[current].classList.add('on');
    dots[current]?.classList.add('on');
  }, SHOT_INTERVAL_MS);
}

function initSignature(): void {
  const rose = document.querySelector('footer .sig-rose');

  if (!rose) {
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('on');
      }
    });
  }, { threshold: 0.6 });

  observer.observe(rose);
}

function initProjectCardExit(): void {
  const site = document.querySelector<HTMLElement>('#site');
  const card = document.querySelector<HTMLAnchorElement>('.pcard');

  if (!site || !card) {
    return;
  }

  // Browsers with cross-document view transitions crossfade the navigation
  // natively (see @view-transition in design-tokens.css) — flash-free without
  // any JS involvement. Only run the manual opacity fade as a fallback where
  // that isn't available.
  const supportsViewTransitions = 'startViewTransition' in document;

  card.addEventListener('click', (event: MouseEvent) => {
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

    if (isModifiedClick || prefersReducedMotion() || supportsViewTransitions) {
      return;
    }

    event.preventDefault();
    site.classList.add('page-exit');

    window.setTimeout(() => {
      window.location.href = card.href;
    }, EXIT_TRANSITION_MS);
  });
}

function initResumeSheet(): void {
  const scrim = document.querySelector<HTMLElement>('#resume-scrim');
  const openButton = document.querySelector<HTMLElement>('#resume-open');
  const closeButton = document.querySelector<HTMLElement>('#resume-close');
  const sheet = scrim?.querySelector<HTMLElement>('.sheet');

  if (!scrim || !openButton || !closeButton || !sheet) {
    return;
  }

  const open = (): void => {
    scrim.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  const close = (): void => {
    scrim.classList.remove('open');
    document.body.style.overflow = '';
  };

  openButton.addEventListener('click', open);
  closeButton.addEventListener('click', close);
  scrim.addEventListener('click', close);
  sheet.addEventListener('click', (event) => event.stopPropagation());

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      close();
    }
  });
}

inject();
initPageEnter();
initCarousel();
initSignature();
initProjectCardExit();
initResumeSheet();
